import { useTranslation } from 'react-i18next';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import SelectField from '../../../../core/components/Fields/SelectField.tsx';
import { useOutputMapperEditor } from '../OutputMapperEditorProvider.tsx';
import { useActionUpdate } from '../hooks/useActionUpdate.ts';
import type { FunctionCallAction } from '../types.ts';

interface FunctionCallBlockProps {
    action: FunctionCallAction;
    mappingIndex: number;
    actionIndex: number;
    onUpdate?: (updates: Partial<FunctionCallAction>) => void;
}

const FunctionCallBlock = ({ action, mappingIndex, actionIndex, onUpdate }: FunctionCallBlockProps) => {
    const { t } = useTranslation('integrations');
    const { config } = useOutputMapperEditor();
    const handleUpdate = useActionUpdate<FunctionCallAction>(mappingIndex, actionIndex, onUpdate);

    const customFunctions = config.custom_functions || [];

    const handleArgumentsChange = (value: string) => {
        try {
            const args = JSON.parse(value);
            handleUpdate({ arguments: args });
        } catch {
            // Keep as string if invalid JSON
        }
    };

    return (
        <div className="flex flex-col gap-3">
            <div>
                <label className="text-xs text-content-weak mb-1 block">
                    {t('outputMapper.visualEditor.functionName')}
                </label>
                {customFunctions.length > 0 ? (
                    <SelectField
                        value={action.function_name}
                        onValueChange={(name) => handleUpdate({ function_name: name })}
                        options={customFunctions.map((f) => ({ value: f, label: f }))}
                    />
                ) : (
                    <StringField
                        value={action.function_name}
                        onValueChange={(name) => handleUpdate({ function_name: name })}
                        placeholder={t('outputMapper.visualEditor.enterFunctionName')}
                    />
                )}
            </div>

            <div>
                <label className="text-xs text-content-weak mb-1 block">
                    {t('outputMapper.visualEditor.arguments')}
                </label>
                <textarea
                    value={action.arguments ? JSON.stringify(action.arguments, null, 2) : ''}
                    onChange={(e) => handleArgumentsChange(e.target.value)}
                    className="w-full h-24 px-2 py-1.5 text-sm border border-line-weak rounded bg-surface-weak text-content-strong font-mono resize-none"
                    placeholder='{"arg1": "value1", "arg2": "value2"}'
                />
            </div>
        </div>
    );
};

export default FunctionCallBlock;
